import React, { useState, useEffect, useRef } from 'react';
import { Send, X, Loader2, Zap, ArrowRight, Activity, BrainCircuit, Terminal } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import FoxLogo from './FoxLogo';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const FoxChatOverlay = () => {
  const { user } = useAuth();
  const { t } = useLanguage(); // Dil fonksiyonunu al
  
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Dil değiştiğinde veya kullanıcı değiştiğinde karşılama mesajını güncelle
  useEffect(() => {
      const userName = user?.name?.split(' ')[0] || 'Captain';
      const welcomeMsg = (t('chat_welcome') || `Hello ${userName}!`).replace('{name}', userName);
      
      setMessages([
        { id: 1, type: 'bot', text: `${t('chat_system_online') || 'Systems Online.'} ${welcomeMsg}` }
      ]);
  }, [user, t]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, loading]);

  useEffect(() => {
    const handleOpenRequest = (e) => {
        setIsOpen(true);
        if (e.detail?.message) {
            setMessages(prev => [...prev, { 
                id: Date.now(), 
                type: 'bot', 
                text: typeof e.detail.message === 'string' ? e.detail.message : (t('chat_default_greeting') || "My intelligence is at your service.") 
            }]);
        }
    };
    window.addEventListener('openFoxChat', handleOpenRequest);
    return () => window.removeEventListener('openFoxChat', handleOpenRequest);
  }, [t]);

  const handleSend = async () => {
    if (!inputText.trim() || loading) return;

    const userMsg = { id: Date.now(), type: 'user', text: inputText };
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setLoading(true);

    try {
      const response = await apiService.ai.chat(userMsg.text);
      const answerText = response.data?.answer || response.answer || "Connection established but empty response.";
      setMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', text: answerText }]);
    } catch (error) {
      console.error("🔴 CHAT ERROR:", error);
      let errorMessage = t('chat_connection_error') || "Connection interrupted.";
      setMessages(prev => [...prev, { id: Date.now() + 1, type: 'bot', text: `${errorMessage} ${t('chat_retry') || 'Please try again.'}` }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-24 right-6 z-[200] flex flex-col items-end pointer-events-none md:bottom-8">
      <div className="pointer-events-auto">
        <AnimatePresence>
          {isOpen && (
            <motion.div 
              initial={{ opacity: 0, y: 40, scale: 0.9, rotate: 2 }}
              animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, y: 40, scale: 0.9 }}
              className="mb-6 w-[360px] h-[600px] bg-[#0f172a]/95 backdrop-blur-3xl rounded-[2.5rem] shadow-[0_25px_70px_-15px_rgba(0,0,0,0.6)] border border-white/10 flex flex-col overflow-hidden ring-1 ring-white/5 relative"
            >
              
              {/* Background Glow */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-[80px] pointer-events-none"></div>

              {/* Header */}
              <div className="bg-[#020617]/50 px-6 py-5 flex items-center justify-between text-white relative z-10 border-b border-white/5">
                <div className="flex items-center gap-4">
                  <div className="relative">
                      <div className="bg-orange-600 p-2 rounded-xl shadow-[0_0_15px_#ea580c]">
                        <FoxLogo size={22} className="text-white" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-emerald-500 w-3 h-3 rounded-full border-2 border-[#020617] animate-pulse"></div>
                  </div>
                  <div>
                    <h3 className="font-black text-xs uppercase tracking-widest text-slate-200 flex items-center gap-2">
                        {t('chat_fox_intelligence') || 'Fox Intelligence'} <BrainCircuit size={12} className="text-orange-500"/>
                    </h3>
                    <p className="text-[9px] font-mono text-emerald-500 mt-0.5">{t('chat_neural_link') || 'NEURAL LINK: ACTIVE'}</p>
                  </div>
                </div>
                <button onClick={() => setIsOpen(false)} className="hover:bg-white/10 p-2 rounded-full transition-all active:scale-90 text-slate-400 hover:text-white">
                    <X size={18}/>
                </button>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar relative z-10" ref={scrollRef}>
                {messages.map((msg) => (
                  <motion.div 
                    initial={{ opacity: 0, x: msg.type === 'user' ? 20 : -20, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    key={msg.id} 
                    className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[85%] p-4 rounded-2xl text-xs md:text-sm font-medium leading-relaxed shadow-lg ${
                        msg.type === 'user' 
                        ? 'bg-gradient-to-br from-orange-600 to-orange-500 text-white rounded-br-none shadow-orange-900/20' 
                        : 'bg-[#1e293b] text-slate-300 rounded-bl-none border border-white/5'
                    }`}>
                      {msg.text}
                    </div>
                  </motion.div>
                ))}
                
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-[#1e293b] px-4 py-3 rounded-2xl rounded-bl-none border border-white/5 flex gap-1.5 items-center">
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-bounce" />
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-bounce [animation-delay:0.2s]" />
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-bounce [animation-delay:0.4s]" />
                    </div>
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="p-4 bg-[#020617]/50 border-t border-white/5 relative z-10">
                <div className="relative flex items-center group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-purple-600 rounded-2xl opacity-20 group-focus-within:opacity-50 transition-opacity blur"></div>
                    <input 
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                      placeholder={t('chat_placeholder') || "Ask the Fox..."} 
                      className="relative w-full h-12 bg-[#0f172a] border border-white/10 rounded-2xl px-5 pr-12 text-xs font-bold text-white focus:outline-none placeholder:text-slate-600 transition-all"
                    />
                    <button 
                      onClick={handleSend} 
                      disabled={loading || !inputText.trim()} 
                      className="absolute right-2 p-2 bg-white/10 hover:bg-orange-600 text-white rounded-xl transition-all active:scale-90 disabled:opacity-0"
                    >
                      <ArrowRight size={16} strokeWidth={3} />
                    </button>
                </div>
                <div className="mt-2 flex justify-center items-center gap-2 opacity-40">
                    <Terminal size={10} className="text-emerald-500" />
                    <span className="text-[8px] font-mono uppercase tracking-[0.2em] text-slate-500">Gemini 2.0 Encrypted Channel</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Floating Action Button (FAB) */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-16 h-16 rounded-[2rem] shadow-[0_0_40px_rgba(234,88,12,0.4)] flex items-center justify-center transition-all duration-500 border-2 border-white/10 relative overflow-hidden group ${
            isOpen 
            ? 'bg-[#0f172a] text-white rotate-90' 
            : 'bg-[#020617]'
          }`}
        >
          {/* Neon Pulse */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-purple-500/20 animate-pulse"></div>
          
          <div className="relative z-10">
              {isOpen ? <X size={28} /> : <FoxLogo size={36} className="text-orange-500 drop-shadow-[0_0_10px_rgba(234,88,12,0.8)]" />}
          </div>
          
          {!isOpen && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 border-2 border-[#020617] rounded-full animate-ping"></div>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default FoxChatOverlay;